// iter164.5 — Hook d'auto-complétion NPA → Localité(s)
// Utilisé par ClientForm.jsx et QuickClientDialog.jsx.

import { useEffect, useRef, useState } from "react";
import { lookupLocality } from "@/lib/npa";

/**
 * Debounce + fetch openplzapi/geo.api.gouv.fr et gère l'auto-fill de la localité.
 *
 * @param {string} npa
 * @param {string} pays
 * @param {string} currentLocalite - Valeur actuelle du champ localité
 * @param {(name: string) => void} onLocaliteAutoFill - Appelé quand un match unique.
 * @returns {{options: Array, loading: boolean, hasMultiple: boolean}}
 */
export function useNpaLookup(npa, pays, currentLocalite, onLocaliteAutoFill) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastKey = useRef("");
  const autoFilledFor = useRef(""); // Évite de re-remplir en boucle

  useEffect(() => {
    const key = `${npa}|${pays}`;
    if (key === lastKey.current) return;
    lastKey.current = key;
    if (!npa || !pays) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const rawResults = await lookupLocality(npa, pays);
        // iter164.5 fix — Dédupliquer par nom : openplzapi renvoie 1 entrée par
        // commune administrative, donc plusieurs "Meyrin" possibles pour un même NPA.
        // Pour l'UI, un même nom = 1 seule option.
        const seen = new Set();
        const results = [];
        for (const r of rawResults) {
          const nameKey = (r.name || "").trim().toLowerCase();
          if (!nameKey || seen.has(nameKey)) continue;
          seen.add(nameKey);
          results.push(r);
        }
        if (cancelled) return;
        setOptions(results);
        // Auto-fill si UN seul résultat (post-dédup) et localité vide
        if (results.length === 1 && key !== autoFilledFor.current) {
          const unique = results[0].name;
          if (!currentLocalite || currentLocalite.trim() === "") {
            onLocaliteAutoFill(unique);
            autoFilledFor.current = key;
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350); // debounce
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npa, pays]);

  return { options, loading, hasMultiple: options.length > 1 };
}
